/**
 * Null payment_sub_state policy by entrypoint:
 * - resolvePaymentDestination (shared / PI / notification): null → order_detail
 * - resolveCheckoutPaymentDestination (checkout-new BT success): null → payment_instruction
 * - resolveMyOrderPaymentDestination (My Order CTA): WFP + null → treated as instruction
 *
 * Unpaid bank-transfer stay on payment until Kirim (`done`):
 * instruction and proof_draft both resolve to payment_instruction when FF is on.
 */
import type { PaymentSubState } from '@diamond/shared/types';

interface PaymentRouteIdentifiers {
  orderMetaId: string;
  warehouse: string;
}

interface ResolvePaymentDestinationArgs {
  paymentSubState: PaymentSubState;
  isPaymentInstructionEnabled: boolean;
  orderMetaId?: string;
  warehouse?: string;
}

export type PaymentDestinationKind =
  | 'payment_instruction'
  | 'order_detail_proof'
  | 'order_detail'
  | 'unpaid_orders';

export interface PaymentDestination {
  kind: PaymentDestinationKind;
  href: string;
}

const encodeRouteSegment = (value: string) => encodeURIComponent(value);

const buildWarehouseQuery = (warehouse: string) =>
  `warehouse=${encodeURIComponent(warehouse)}`;

export const buildPaymentInstructionRoute = ({
  orderMetaId,
  warehouse,
}: PaymentRouteIdentifiers) =>
  `/payment/${encodeRouteSegment(orderMetaId)}?${buildWarehouseQuery(
    warehouse
  )}`;

export const buildOrderDetailRoute = ({
  orderMetaId,
  warehouse,
}: PaymentRouteIdentifiers) =>
  `/order-detail/${encodeRouteSegment(orderMetaId)}?${buildWarehouseQuery(
    warehouse
  )}`;

export const buildOrderDetailPaymentProofRoute = (
  identifiers: PaymentRouteIdentifiers
) => `${buildOrderDetailRoute(identifiers)}#submit-payment-proof`;

export const buildIntentionalPoDetailRoute = ({
  orderMetaId,
  warehouse,
}: PaymentRouteIdentifiers) =>
  `/order-detail/${encodeRouteSegment(orderMetaId)}?${buildWarehouseQuery(
    warehouse
  )}&view=po-detail`;

export const buildIntentionalPoDetailPaymentProofRoute = (
  identifiers: PaymentRouteIdentifiers
) => `${buildIntentionalPoDetailRoute(identifiers)}#submit-payment-proof`;

export const buildUnpaidMyOrderRoute = () =>
  '/my-order?page=1&search=&group_status=unpaid';

/**
 * Domain router for payment surfaces. Requires schema-backed sub-state —
 * callers pass PaymentSubState from @diamond/shared/types.
 */
export const resolvePaymentDestination = ({
  paymentSubState,
  isPaymentInstructionEnabled,
  orderMetaId,
  warehouse,
}: ResolvePaymentDestinationArgs): PaymentDestination => {
  if (!orderMetaId?.trim() || !warehouse?.trim()) {
    return {
      kind: 'unpaid_orders',
      href: buildUnpaidMyOrderRoute(),
    };
  }

  const identifiers = { orderMetaId, warehouse };

  if (!isPaymentInstructionEnabled) {
    return {
      kind: 'order_detail_proof',
      href: buildOrderDetailPaymentProofRoute(identifiers),
    };
  }

  switch (paymentSubState) {
    case 'instruction':
    case 'proof_draft':
      return {
        kind: 'payment_instruction',
        href: buildPaymentInstructionRoute(identifiers),
      };
    case 'done':
    case null:
      return {
        kind: 'order_detail',
        href: buildOrderDetailRoute(identifiers),
      };
    default: {
      const _exhaustive: never = paymentSubState;
      return _exhaustive;
    }
  }
};

/**
 * Checkout-entry destination after a successful bank-transfer submit.
 * Treats explicit `null` sub_state as payment instruction so buyers
 * do not flash Order Detail while waiting-payment truth catches up.
 * Does not change shared resolver semantics used by My Order / OD guards.
 */
export const resolveCheckoutPaymentDestination = ({
  paymentSubState,
  isPaymentInstructionEnabled,
  orderMetaId,
  warehouse,
}: ResolvePaymentDestinationArgs): PaymentDestination => {
  if (
    isPaymentInstructionEnabled &&
    paymentSubState === null &&
    orderMetaId?.trim() &&
    warehouse?.trim()
  ) {
    return {
      kind: 'payment_instruction',
      href: buildPaymentInstructionRoute({ orderMetaId, warehouse }),
    };
  }

  return resolvePaymentDestination({
    isPaymentInstructionEnabled,
    orderMetaId,
    paymentSubState,
    warehouse,
  });
};
